import { FC } from 'react';
import { rupiah } from '@lib/currency';
import BusinessName from '../BusinessName';
import Spacer from '@components/Spacer';
import Badge from '@components/Badge';
import { useCheckoutStep } from '../../hooks/useCheckoutStep';
import { DataDetailCheckout, SettingsProps } from '@pages/types';

interface PaymentOverviewProps {
  data: DataDetailCheckout;
  settings: SettingsProps;
}

const PaymentOverview: FC<PaymentOverviewProps> = ({ data, settings }) => {
  const { goToMethod, goToForm } = useCheckoutStep();

  return (
    <div className="mb-5 flex flex-col gap-4 px-3 pt-8 md:mb-0 md:pb-4 lg:px-0">
      <BusinessName
        data={data}
        title="Payment Overview"
        subtitle={`Complete your payment to ${data?.business?.name || ''}`}
      />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {data?.customer && (
          <div className="min-w-0 rounded-xl border border-gray-200 p-4">
            <div className="space-y-3">
              {data.customer.name && (
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    Name
                  </span>
                  <span className="break-words text-sm font-medium capitalize text-gray-900">
                    {data.customer.name}
                  </span>
                </div>
              )}

              {data.customer.email && (
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    Email
                  </span>
                  <span className="break-all text-sm font-medium text-gray-900">
                    {data.customer.email}
                  </span>
                </div>
              )}

              {data.customer.phone && (
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    Phone
                  </span>
                  <span className="break-words text-sm font-medium text-gray-900">
                    {data.customer.phone}
                  </span>
                </div>
              )}

              {data.customer.address && (
                <div className="flex min-w-0 flex-col gap-1">
                  <span className="text-xs font-medium text-gray-500">
                    Address
                  </span>
                  <span className="break-words text-sm font-medium text-gray-900">
                    {data.customer.address}
                  </span>
                </div>
              )}
            </div>

            <button
              onClick={goToForm}
              className="mt-6 w-full rounded-lg border-2 px-4 py-2 text-sm font-semibold transition-colors"
              style={{
                backgroundColor: settings.button_color || '#5D87FF',
                color: settings.button_text_color || '#FFFFFF',
              }}
            >
              Edit Information
            </button>
          </div>
        )}

        <div className="grid h-fit min-w-0 grid-cols-2 gap-6 lg:grid-cols-2">
          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Merchant
            </span>
            <span className="break-words text-base font-semibold capitalize text-gray-900">
              {data?.merchant?.name || '-'}
            </span>
          </div>

          <div className="flex min-w-0 flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Status
            </span>
            <div className="pt-1">
              <Badge status={data?.status} />
            </div>
          </div>

          <div className="col-span-2 flex min-w-0 flex-col gap-1">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              Invoice ID
            </span>
            <span className="break-all text-base font-semibold text-gray-900">
              {data?.invoice_number || '-'}
            </span>
          </div>
        </div>
      </div>

      <Spacer />

      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
          Amount Due
        </span>
        <span
          className="break-words text-3xl font-semibold capitalize"
          style={{ color: settings?.sidebar_text_color }}
        >
          {rupiah(data?.total ?? 0)}
        </span>
      </div>

      <div className="fixed bottom-1 left-0 right-0 z-20 bg-white px-4 py-3 md:relative md:px-0">
        <div className="flex flex-col items-center justify-between gap-4 md:flex-row lg:gap-4">
          <button
            onClick={goToForm}
            className="w-full rounded-lg border border-[#D4D4D4] bg-[#FAFAFA] px-4 py-3 font-semibold text-black transition-opacity hover:opacity-90"
          >
            Back
          </button>
          <button
            onClick={goToMethod}
            className="w-full rounded-lg px-4 py-3 font-semibold text-white transition-opacity hover:opacity-90"
            style={{
              backgroundColor: settings.button_color || '#5D87FF',
              color: settings.button_text_color || '#FFFFFF',
            }}
          >
            Choose Payment Method
          </button>
        </div>
      </div>
    </div>
  );
};

export default PaymentOverview;
